import { useState, useEffect, useRef } from 'react';
import { USER_EXPERIENCE_STATES } from '@/constants/appConstants';

/**
 * Component to test consistent hook execution order across different view modes
 * This simulates the conditions that previously caused hooks to execute in 
 * inconsistent order, and validates our fix works correctly
 */
export function HookExecutionTester() {
  // Track execution order of hooks
  const hookExecutionOrder = useRef<string[]>([]);
  
  // Track component renders
  const renderCount = useRef(0);
  
  // Define the possible view mode types
  type ViewModeType = typeof USER_EXPERIENCE_STATES.FIRST_RUN | typeof USER_EXPERIENCE_STATES.SECOND_RUN;
  
  // State to toggle between view modes with explicit typing
  const [viewMode, setViewMode] = useState<ViewModeType>(USER_EXPERIENCE_STATES.FIRST_RUN);
  
  // State to toggle loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // First hook - should always execute
  useEffect(() => {
    hookExecutionOrder.current.push('Hook 1');
    
    // Simulate cleanup
    return () => {
      hookExecutionOrder.current.push('Hook 1 Cleanup');
    };
  }, []);
  
  // Second hook - should always execute after first hook
  useEffect(() => {
    hookExecutionOrder.current.push('Hook 2');
    
    return () => {
      hookExecutionOrder.current.push('Hook 2 Cleanup');
    };
  }, [viewMode]);
  
  // Third hook - should always execute after second hook
  useEffect(() => {
    hookExecutionOrder.current.push('Hook 3');
    
    return () => {
      hookExecutionOrder.current.push('Hook 3 Cleanup');
    };
  }, [isLoading]);
  
  // Track render count
  useEffect(() => {
    renderCount.current += 1;
  });
  
  // Toggle view mode
  const toggleViewMode = () => {
    setViewMode(current => 
      current === USER_EXPERIENCE_STATES.FIRST_RUN 
        ? USER_EXPERIENCE_STATES.SECOND_RUN 
        : USER_EXPERIENCE_STATES.FIRST_RUN
    );
  };
  
  // Toggle loading state
  const toggleLoading = () => {
    setIsLoading(current => !current);
  };
  
  // Reset execution order
  const resetExecutionOrder = () => {
    hookExecutionOrder.current = [];
    renderCount.current = 0;
  };
  
  return (
    <div className="p-6 border rounded-lg shadow-md bg-white">
      <h2 className="text-2xl font-bold mb-4">Hook Execution Order Tester</h2>
      
      <div className="mb-4">
        <div className="flex items-center mb-2">
          <span className="font-semibold mr-4">Current View Mode:</span>
          <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full">
            {viewMode === USER_EXPERIENCE_STATES.FIRST_RUN ? 'First Run' : 'Second Run'}
          </span>
        </div>
        
        <div className="flex items-center">
          <span className="font-semibold mr-4">Loading State:</span>
          <span className={`px-3 py-1 rounded-full ${isLoading 
            ? 'bg-amber-100 text-amber-800'
            : 'bg-green-100 text-green-800'}`}>
            {isLoading ? 'Loading' : 'Loaded'}
          </span>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-6">
        <button 
          onClick={toggleViewMode}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          aria-label="Toggle between first run and second run view modes"
        >
          Toggle View Mode
        </button>
        
        <button 
          onClick={toggleLoading}
          className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-opacity-50"
          aria-label="Toggle loading state"
        >
          Toggle Loading
        </button>
        
        <button 
          onClick={resetExecutionOrder}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
          aria-label="Reset execution order tracking"
        >
          Reset Execution Order
        </button>
      </div>
      
      <div className="border p-4 rounded bg-gray-50">
        <h3 className="text-lg font-semibold mb-2">Hook Execution Order:</h3>
        <div className="mb-2">
          <span className="font-semibold">Render Count:</span> {renderCount.current}
        </div>
        
        <ul className="list-disc pl-6 space-y-1">
          {hookExecutionOrder.current.map((hook, index) => (
            <li key={index} className={hook.includes('Cleanup') ? 'text-red-600' : 'text-green-600'}>
              {hook}
            </li>
          ))}
        </ul>
        
        {hookExecutionOrder.current.length === 0 && (
          <p className="text-gray-500 italic">No hooks executed yet</p>
        )}
      </div>
      
      <div className="mt-6 p-4 border rounded bg-yellow-50 text-yellow-800">
        <h3 className="text-lg font-semibold mb-2">Expected Behavior:</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Hook 1, Hook 2, Hook 3 should always execute in that order</li>
          <li>Toggling view mode should trigger Hook 2</li>
          <li>Toggling loading should trigger Hook 3</li>
          <li>All hooks should execute on every render (no skipped hooks)</li>
        </ul>
      </div>
      
      {/* Conditional rendering section to demonstrate proper pattern */}
      <div className="mt-6 p-4 border rounded bg-blue-50 text-blue-800">
        <h3 className="text-lg font-semibold mb-2">Component Content:</h3>
        {isLoading ? (
          <div className="p-4 bg-gray-200 animate-pulse rounded">
            Loading content...
          </div>
        ) : (
          <div className="p-4 bg-white border rounded">
            <p>Main content for view mode: {viewMode === USER_EXPERIENCE_STATES.FIRST_RUN ? 'First Run' : 'Second Run'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
